import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {BaseComponent} from "../../../../BaseComponent";
import {DeviceTypeLite} from "../../../../../models/device-type/DeviceTypeLite";
import {AcquisitionCronFrequencyEnum} from "../../../../../enums/registry/AcquisitionCronFrequencyEnum";
import {Gateway} from "../../../../../models/gateway/Gateway";
import {DeviceDraft, MetricGroupInstanceDraft} from "../../../../../models/device/DeviceDraft";
import {DxFormComponent} from "devextreme-angular";
import {MeasureGroupTableComponent} from "../measure-group-table/measure-group-table.component";
import {MeasureMetricTableComponent} from "../measure-metric-table/measure-metric-table.component";
import {RowStateTracker} from "../../../../../utils/RowStateTracker";
import {getRowKey} from "../../../../../utils/rowKey";

@Component({
  selector: 'app-device-edit-form',
  templateUrl: './device-edit-form.component.html',
  styleUrls: ['./device-edit-form.component.css', '../../gateway-device.component.css', '../../../../common/drawer-style.css', '../../../base-component-with-drawer.component.css']
})
export class DeviceEditFormComponent extends BaseComponent implements OnChanges {


  @Output() selectedMetricGroupChange = new EventEmitter<MetricGroupInstanceDraft>();

  get selectedMetricGroup(): MetricGroupInstanceDraft | undefined {
    return this._selectedMetricGroup;
  }

  @Input() set selectedMetricGroup(value: MetricGroupInstanceDraft | undefined) {
    this._selectedMetricGroup = value;
    this.selectedMetricGroupChange.emit(value);
  }

  get editedDeviceFull(): DeviceDraft {
    return this._editedDeviceFull;
  }

  @Input() set editedDeviceFull(value: DeviceDraft) {
    if (value && value.defaultType == null)
      value.defaultType = value.typeId;
    this._editedDeviceFull = value;
    this.updateFilteredDeviceTypes();
  }

  private _editedDeviceFull!: DeviceDraft;
  @Input() selectedDeviceFull!: DeviceDraft;
  @Input() selectedGateway?: Gateway;
  @Input() deviceEditTextOptions: any;
  @Input() deviceEditTextAreaOptions: any;
  @Input() deviceEditNumberOptions: any;
  @Input() deviceEditCheckBoxOptions: any;
  @Input() selectedRowMetricGroup: any;
  @Input() deviceTypes?: DeviceTypeLite[];
  @Input() isEditMode!: boolean;

  @Output() isEditFormValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() deviceEdited: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('editDeviceForm', {static: false}) editDeviceForm!: DxFormComponent;
  @ViewChild('groupMetricDataGrid', {static: false}) groupMetricDataGrid?: MeasureGroupTableComponent;
  @ViewChild('metricDataGrid', {static: false}) metricDataGrid?: MeasureMetricTableComponent;

  private _selectedMetricGroup?: MetricGroupInstanceDraft;

  protected acquisitionCronFrequencyValues: string[];

  constructor() {
    super();
    this.acquisitionCronFrequencyValues = Object.values(AcquisitionCronFrequencyEnum);
  }

  // Metrics-tab grids (groups + metrics); only the metrics one needs a selected group to exist.
  private get metricTables(): (MeasureGroupTableComponent | MeasureMetricTableComponent)[] {
    return [this.groupMetricDataGrid, this.metricDataGrid]
      .filter((t): t is MeasureGroupTableComponent | MeasureMetricTableComponent => !!t);
  }

  readonly metricRowStateTracker = new RowStateTracker();
  protected readonly getRowKey = getRowKey;

  focusActiveMetricEdit(): void {
    this.metricDataGrid?.focusActiveEdit();
  }

  // The metric tracker is read from this form, not from the metric table: the table is destroyed
  // whenever no group is selected, and the tracker keeps every group's rows through that.
  get rowStateTrackers(): RowStateTracker[] {
    return [this.groupMetricDataGrid?.rowStateTracker, this.metricRowStateTracker]
      .filter((t): t is RowStateTracker => !!t);
  }

  cancelActiveRowEdits(): void {
    this.metricTables.forEach(t => t.cancelActiveEdit());
  }

  resetRowStates(): void {
    this.rowStateTrackers.forEach(t => t.resetAll());
  }

  /**
   * Check if edited gateway is valid, to enable SAVE button
   */
  checkEditDeviceValidation() {

    if (this.editDeviceForm?.instance) {
      const result = this.editDeviceForm.instance.validate();
      this.isEditFormValid.emit(result.isValid);
      const deviceEdited = JSON.stringify(this.editedDeviceFull) !== JSON.stringify(this.selectedDeviceFull);
      this.deviceEdited.emit(deviceEdited);
      this.appService.setRegistryEntityIsChanged(deviceEdited && this.isEditMode);
    }

  }

  onGroupsChanged() {
    if (this.selectedMetricGroup && !this.editedDeviceFull.metricGroupInstances?.some(g => getRowKey(g) === getRowKey(this.selectedMetricGroup)))
      this.selectedMetricGroup = undefined;
    this.metricDataGrid?.refresh();
    this.checkEditDeviceValidation();
  }

  validateDeviceName(event: any) {
    return /^[a-z0-9\s_-]+$/i.test(event.value);
  }


  getAcquisitionCronLabel = (acquisitionCron: string) => {
    return this.translate.instant('FREQUENCY.' + acquisitionCron);
  };

  // Stable field, not a getter - bound into an inline `editorOptions` object literal in the
  // template. A getter would return a fresh array every change-detection cycle, which makes
  // DevExtreme treat the select box's dataSource as changed and reset the widget's popup/list
  // state - including mid-click while the user is picking an item, so the selection never sticks.
  filteredDeviceTypes: DeviceTypeLite[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['deviceTypes'])
      this.updateFilteredDeviceTypes();
  }

  private updateFilteredDeviceTypes() {
    this.filteredDeviceTypes = (this.deviceTypes ?? []).filter(dt => dt.protocolType === this.editedDeviceFull?.protocolType);
  }

  resetForm() {
    this.selectedRowMetricGroup = [];
    this.selectedMetricGroup = undefined;
  }
}
